import React, { Component, ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import i18n from '../i18n/i18n';

/*
 * ErrorBoundary (React Class Component)
 * ======================================
 * Catches JavaScript errors anywhere in its child component tree,
 * logs them, and displays a fallback UI instead of crashing the entire app.
 *
 * This is DIFFERENT from the router-level ErrorBoundary (src/ErrorBoundary.tsx)
 * which only handles loader/action errors. This catches RENDER errors.
 *
 * Three usage levels:
 * 1. App-level: wraps the entire app — last resort, shows full-page fallback
 * 2. Page-level: wraps page content — preserves sidebar/nav if page crashes
 * 3. Widget-level: wraps individual cards/widgets — keeps other widgets alive
 *
 * Usage:
 *   <ErrorBoundary>
 *     <MyComponent />
 *   </ErrorBoundary>
 *
 *   // Inline variant for widgets:
 *   <ErrorBoundary inline>
 *     <DashboardCard />
 *   </ErrorBoundary>
 *
 * Note: Error boundaries do NOT catch errors in:
 *   - Event handlers (use try/catch)
 *   - Async code (use .catch())
 *   - Server-side rendering
 *   - Errors in the boundary itself
 *
 * We use a class component because React has no hook equivalent for
 * componentDidCatch / getDerivedStateFromError (as of React 18).
 */

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback UI. If omitted, uses the default error page. */
  fallback?: ReactNode;
  /** Optional callback when an error is caught — useful for error reporting services */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** If true, shows a compact inline error instead of full-page */
  inline?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class AppErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
    this.props.onError?.(error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Custom fallback takes precedence
    if (this.props.fallback) {
      return this.props.fallback;
    }

    // Inline variant — compact, for widget-level boundaries
    if (this.props.inline) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.08)' : '#fef2f2',
            border: '1px solid',
            borderColor: 'error.light',
            borderRadius: 'var(--radius-lg)',
            gap: 1.5,
          }}
        >
          <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 20 }} />
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            {i18n.t('errors.section')}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={this.handleReset}
            sx={{ textTransform: 'none', fontSize: 'var(--font-size-xs)' }}
          >
            {i18n.t('common.actions.tryAgain')}
          </Button>
        </Box>
      );
    }

    // Default full-page error UI
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          p: 6,
          textAlign: 'center',
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 'var(--radius-full)',
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.12)' : '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 32 }} />
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 'var(--font-weight-semibold)',
            color: 'text.primary',
            mb: 1,
          }}
        >
          {i18n.t('errors.title')}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            mb: 4,
            maxWidth: 420,
            lineHeight: 'var(--line-height-relaxed)',
          }}
        >
          {i18n.t('errors.unexpected')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={this.handleReset}
            sx={{ textTransform: 'none', fontWeight: 'var(--font-weight-medium)' }}
          >
            {i18n.t('common.actions.goBack')}
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={this.handleReload}
            sx={{ textTransform: 'none', fontWeight: 'var(--font-weight-medium)' }}
          >
            {i18n.t('errors.refreshPage')}
          </Button>
        </Box>

        {/* Show error details in development only */}
        {process.env.NODE_ENV === 'development' && this.state.error && (
          <Box
            component="details"
            sx={{
              mt: 4,
              p: 2,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 'var(--radius-md)',
              maxWidth: 600,
              width: '100%',
              textAlign: 'left',
            }}
          >
            <Typography
              component="summary"
              variant="body2"
              sx={{
                fontWeight: 'var(--font-weight-medium)',
                color: 'text.secondary',
                cursor: 'pointer',
                mb: 1,
              }}
            >
              {i18n.t('errors.details')}
            </Typography>
            <Box
              component="pre"
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--font-size-xs)',
                color: 'error.main',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                m: 0,
              }}
            >
              {this.state.error.message}
              {'\n\n'}
              {this.state.error.stack}
            </Box>
          </Box>
        )}
      </Box>
    );
  }
}

export default AppErrorBoundary;
